import ArrowRight from "@/assets/icons/ArrowRight";
import FlexPass from "@/assets/icons/FlexPass";
import AppButton from "@/components/shared/AppButton";
import { Card } from "@/components/shared/Card";
import { HStack } from "@/components/ui/hstack";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { T, useT } from "@transifex/react";
import { useRouter } from "expo-router";
import type { Pass } from "ipctg-inflow-types/InFlowDB";

export interface PassCardProps {
  pass: Pass;
  isLoading?: boolean;
}

export function isPassExpired(validTo: string): boolean {
  return new Date() > new Date(validTo);
}

const PassCard = ({ pass, isLoading }: PassCardProps) => {
  const router = useRouter();
  const t = useT();

  if (isLoading) {
    return <PassCardSkeleton />;
  }

  return (
    <Card className="pb-[24px] pt-[20px]">
      <HStack className="gap-[8px]">
        <FlexPass />
        <Text className="font-[700] text-[12px] text-typography-900 tracking-[0.13em] flex items-center relative top-0.5">
          <T _str="FLEX PARK" />
        </Text>
      </HStack>
      <Text className="font-[700] text-[20px] text-typography-900">{pass.passName}</Text>
      <VStack className="gap-[2px]">
        <Text className="font-[700] leading-[20px] text-[14px] text-typography-900">{pass.locationName}</Text>
        <Text className="font-[400] leading-[20px] text-[14px] text-typography-900">{pass.locationAddress.address}</Text>
      </VStack>
      <Text className={`font-[700] leading-[20px] text-[14px] text-typography-900 ${pass.maxUses > pass.uses ? "" : "text-warning-700"}`}>
        {isPassExpired(pass.validTo) ? "Expired" : t("{left}/{max} Passes Left", { left: pass.maxUses - pass.uses, max: pass.maxUses })}
      </Text>
      <AppButton
        icon={ArrowRight}
        iconPosition="after"
        onPress={() => router.push({ pathname: "/my-passes/detail", params: { pass: JSON.stringify(pass) } })}
        title={t("More info")}
      />
    </Card>
  );
};

const PassCardSkeleton = () => {
  return (
    <Card className="pb-[24px] pt-[20px]">
      <HStack className="gap-[8px] items-center">
        <Skeleton className="h-[24px] w-[24px]" />
        <SkeletonText className="h-[12px] w-[100px]" />
      </HStack>
      <SkeletonText className="h-[20px] w-[200px]" />
      <VStack className="gap-[10px]">
        <SkeletonText className="h-[14px] w-[180px]" />
        <SkeletonText className="h-[14px] w-[220px]" />
      </VStack>
      <SkeletonText className="h-[14px] w-[150px]" />
      <Skeleton className="h-[40px] w-[120px]" />
    </Card>
  );
};

export default PassCard;
